import { useState, useEffect, useCallback } from 'react';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { IconPlus, IconPencil, IconTrash } from '@tabler/icons-react';
import {
  listSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier,
} from '../services/suppliersService';
import {
  getDistricts,
  getAllCounties,
  getCountiesByDistrict,
  getParishesByCounty,
  District,
  County,
  Parish,
} from '../services/locationService';
import { getActiveOperations, type ActiveOperation } from '../services/forestryService';
import type { Supplier } from '../types';

interface FormState {
  name: string;
  districtId: string;
  countyId: string;
  parishId: string;
  servicesOffered: string[];
  rating: string;
  contactEmail: string;
  contactPhone: string;
}

const DEFAULT_FORM: FormState = {
  name: '',
  districtId: '',
  countyId: '',
  parishId: '',
  servicesOffered: [],
  rating: '',
  contactEmail: '',
  contactPhone: '',
};

export default function SuppliersPage() {
  // List state
  const [items, setItems] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');

  // Reference data
  const [districts, setDistricts] = useState<District[]>([]);
  const [allCounties, setAllCounties] = useState<County[]>([]);
  const [operations, setOperations] = useState<ActiveOperation[]>([]);

  // Modal location cascades
  const [modalCounties, setModalCounties] = useState<County[]>([]);
  const [modalParishes, setModalParishes] = useState<Parish[]>([]);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [loadingParishes, setLoadingParishes] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Load reference data once on mount
  useEffect(() => {
    getDistricts()
      .then(setDistricts)
      .catch(err => console.error('Failed to load districts:', err));
    getAllCounties()
      .then(setAllCounties)
      .catch(err => console.error('Failed to load counties:', err));
    getActiveOperations()
      .then(setOperations)
      .catch(err => console.error('Failed to load operations:', err));
  }, []);

  // Reset and reload when filters change
  useEffect(() => {
    setLastDoc(null);
    setItems([]);
    setHasMore(false);
    (async () => {
      setLoading(true);
      try {
        const result = await listSuppliers({
          pageSize: 50,
          lastDoc: null,
          searchQuery,
          districtFilter,
        });
        setItems(result.items);
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);
      } catch (err) {
        console.error('Failed to load suppliers:', err);
        alert('Failed to load suppliers.');
      } finally {
        setLoading(false);
      }
    })();
  }, [searchQuery, districtFilter]);

  const loadMore = useCallback(async () => {
    if (!lastDoc) return;
    setLoading(true);
    try {
      const result = await listSuppliers({ pageSize: 50, lastDoc, searchQuery, districtFilter });
      setItems(prev => [...prev, ...result.items]);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (err) {
      console.error('Failed to load more suppliers:', err);
      alert('Failed to load suppliers.');
    } finally {
      setLoading(false);
    }
  }, [lastDoc, searchQuery, districtFilter]);

  // Handle district change in modal — load counties
  async function onModalDistrictChange(districtId: string) {
    setForm(prev => ({ ...prev, districtId, countyId: '', parishId: '' }));
    setModalCounties([]);
    setModalParishes([]);
    if (!districtId) return;
    setLoadingCounties(true);
    try {
      const counties = await getCountiesByDistrict(districtId);
      setModalCounties(counties);
    } catch (err) {
      console.error('Failed to load counties:', err);
    } finally {
      setLoadingCounties(false);
    }
  }

  // Handle county change in modal — load parishes
  async function onModalCountyChange(countyId: string) {
    setForm(prev => ({ ...prev, countyId, parishId: '' }));
    setModalParishes([]);
    if (!countyId) return;
    setLoadingParishes(true);
    try {
      const parishes = await getParishesByCounty(countyId);
      setModalParishes(parishes);
    } catch (err) {
      console.error('Failed to load parishes:', err);
    } finally {
      setLoadingParishes(false);
    }
  }

  function openAdd() {
    setEditing(null);
    setForm(DEFAULT_FORM);
    setModalCounties([]);
    setModalParishes([]);
    setFormError(null);
    setShowModal(true);
  }

  async function openEdit(supplier: Supplier) {
    setEditing(supplier);
    const districtId = supplier.operatingLocation.district ?? '';
    const countyId = supplier.operatingLocation.county ?? '';
    const parishId = supplier.operatingLocation.parish ?? '';

    setForm({
      name: supplier.name,
      districtId,
      countyId,
      parishId,
      servicesOffered: supplier.servicesOffered,
      rating: supplier.rating !== undefined ? String(supplier.rating) : '',
      contactEmail: supplier.contactEmail ?? '',
      contactPhone: supplier.contactPhone ?? '',
    });

    setModalCounties([]);
    setModalParishes([]);
    if (districtId) {
      setLoadingCounties(true);
      try {
        const counties = await getCountiesByDistrict(districtId);
        setModalCounties(counties);
        if (countyId) {
          setLoadingParishes(true);
          try {
            const parishes = await getParishesByCounty(countyId);
            setModalParishes(parishes);
          } finally {
            setLoadingParishes(false);
          }
        }
      } finally {
        setLoadingCounties(false);
      }
    }

    setFormError(null);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditing(null);
    setFormError(null);
  }

  function toggleService(id: string) {
    setForm(prev => ({
      ...prev,
      servicesOffered: prev.servicesOffered.includes(id)
        ? prev.servicesOffered.filter(s => s !== id)
        : [...prev.servicesOffered, id],
    }));
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setFormError('Name is required.');
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const payload: Omit<Supplier, 'id'> = {
        name: form.name.trim(),
        operatingLocation: {
          district: form.districtId || null,
          county: form.countyId || null,
          parish: form.parishId || null,
        },
        servicesOffered: form.servicesOffered,
        ...(form.rating !== '' && { rating: parseFloat(form.rating) }),
        ...(form.contactEmail.trim() && { contactEmail: form.contactEmail.trim() }),
        ...(form.contactPhone.trim() && { contactPhone: form.contactPhone.trim() }),
      };

      if (editing) {
        await updateSupplier(editing.id, payload);
      } else {
        await createSupplier(payload);
      }

      closeModal();
      const result = await listSuppliers({
        pageSize: 50,
        lastDoc: null,
        searchQuery,
        districtFilter,
      });
      setItems(result.items);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (err) {
      console.error('Failed to save supplier:', err);
      setFormError('Failed to save supplier. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(supplier: Supplier) {
    if (!confirm(`Delete supplier "${supplier.name}"?`)) return;
    try {
      await deleteSupplier(supplier.id);
      setItems(prev => prev.filter(s => s.id !== supplier.id));
    } catch (err) {
      console.error('Failed to delete supplier:', err);
      alert('Failed to delete supplier.');
    }
  }

  function locationLabel(supplier: Supplier): string {
    const loc = supplier.operatingLocation;
    if (!loc.district) return 'Nationwide';
    const districtName = districts.find(d => d.id === loc.district)?.name ?? loc.district;
    if (!loc.county) return districtName;
    const countyName = allCounties.find(c => c.id === loc.county)?.name ?? loc.county;
    const parts: string[] = [districtName, countyName];
    if (loc.parish) parts.push(loc.parish);
    return parts.join(' / ');
  }

  return (
    <div className="page-header">
      <div className="row g-2 align-items-center">
        <div className="col">
          <div className="page-pretitle">Market</div>
          <h2 className="page-title">Suppliers</h2>
        </div>
        <div className="col-auto">
          <button className="btn btn-primary" onClick={openAdd}>
            <IconPlus className="icon" />
            Add Supplier
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mt-3">
        <div className="card-body">
          <div className="row g-2">
            <div className="col-12 col-md-6">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by name…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">District</label>
              <select
                className="form-select"
                value={districtFilter}
                onChange={e => setDistrictFilter(e.target.value)}
              >
                <option value="">All Districts</option>
                {districts.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card mt-3">
        <div className="table-responsive">
          <table className="table table-vcenter card-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Location</th>
                <th>Services</th>
                <th>Rating</th>
                <th>Email</th>
                <th>Phone</th>
                <th className="w-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    <span className="spinner-border spinner-border-sm me-2" />
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty">
                      <p className="empty-title">No suppliers found</p>
                      <p className="empty-subtitle text-muted">Try adjusting your filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                items.map(supplier => (
                  <tr key={supplier.id}>
                    <td>{supplier.name}</td>
                    <td>{locationLabel(supplier)}</td>
                    <td>
                      <span className="badge bg-blue-lt">{supplier.servicesOffered.length}</span>
                    </td>
                    <td>{supplier.rating !== undefined ? supplier.rating.toFixed(1) : '—'}</td>
                    <td>{supplier.contactEmail ?? '—'}</td>
                    <td>{supplier.contactPhone ?? '—'}</td>
                    <td>
                      <div className="btn-list flex-nowrap">
                        <button
                          className="btn btn-icon"
                          title="Edit"
                          onClick={() => openEdit(supplier)}
                        >
                          <IconPencil className="icon" />
                        </button>
                        <button
                          className="btn btn-icon text-danger"
                          title="Delete"
                          onClick={() => handleDelete(supplier)}
                        >
                          <IconTrash className="icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Load More */}
      {hasMore && (
        <div className="text-center mt-3">
          <button className="btn btn-outline-secondary" onClick={loadMore} disabled={loading}>
            {loading ? <span className="spinner-border spinner-border-sm" /> : 'Load More'}
          </button>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div
          className="modal modal-blur fade show"
          tabIndex={-1}
          role="dialog"
          style={{ display: 'block' }}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editing ? 'Edit Supplier' : 'Add Supplier'}</h5>
                <button type="button" className="btn-close" onClick={closeModal} />
              </div>
              <div className="modal-body">
                {formError && <div className="alert alert-danger mb-3">{formError}</div>}
                <div className="row g-3">
                  {/* Name */}
                  <div className="col-12">
                    <label className="form-label required">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.name}
                      onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Supplier name"
                    />
                  </div>

                  {/* Location — all optional */}
                  <div className="col-12">
                    <label className="form-label">
                      Location{' '}
                      <span className="text-muted fw-normal">
                        (optional — leave blank for country-level)
                      </span>
                    </label>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">District</label>
                    <select
                      className="form-select"
                      value={form.districtId}
                      onChange={e => onModalDistrictChange(e.target.value)}
                    >
                      <option value="">— None —</option>
                      {districts.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">County</label>
                    <select
                      className="form-select"
                      value={form.countyId}
                      onChange={e => onModalCountyChange(e.target.value)}
                      disabled={!form.districtId || loadingCounties}
                    >
                      <option value="">— None —</option>
                      {modalCounties.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    {loadingCounties && (
                      <div className="form-hint">
                        <span className="spinner-border spinner-border-sm me-1" />
                        Loading…
                      </div>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Parish</label>
                    <select
                      className="form-select"
                      value={form.parishId}
                      onChange={e => setForm(prev => ({ ...prev, parishId: e.target.value }))}
                      disabled={!form.countyId || loadingParishes || modalParishes.length === 0}
                    >
                      <option value="">— None —</option>
                      {modalParishes.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    {loadingParishes && (
                      <div className="form-hint">
                        <span className="spinner-border spinner-border-sm me-1" />
                        Loading…
                      </div>
                    )}
                  </div>

                  {/* Services Offered */}
                  <div className="col-12">
                    <label className="form-label">Services Offered</label>
                    {operations.length === 0 ? (
                      <div className="text-muted small">
                        <span className="spinner-border spinner-border-sm me-1" />
                        Loading services…
                      </div>
                    ) : (
                      <div
                        className="border rounded p-2"
                        style={{ maxHeight: 200, overflowY: 'auto' }}
                      >
                        {operations.map(op => (
                          <div key={op.id} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`svc-${op.id}`}
                              checked={form.servicesOffered.includes(op.id)}
                              onChange={() => toggleService(op.id)}
                            />
                            <label className="form-check-label" htmlFor={`svc-${op.id}`}>
                              {op.description}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                    {form.servicesOffered.length > 0 && (
                      <div className="form-hint">
                        {form.servicesOffered.length} service
                        {form.servicesOffered.length !== 1 ? 's' : ''} selected
                      </div>
                    )}
                  </div>

                  {/* Rating + Contact */}
                  <div className="col-md-4">
                    <label className="form-label">Rating (0–5)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.rating}
                      onChange={e => setForm(prev => ({ ...prev, rating: e.target.value }))}
                      min={0}
                      max={5}
                      step={0.1}
                      placeholder="e.g. 4.5"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Contact Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={form.contactEmail}
                      onChange={e => setForm(prev => ({ ...prev, contactEmail: e.target.value }))}
                      placeholder="supplier@example.com"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Contact Phone</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.contactPhone}
                      onChange={e => setForm(prev => ({ ...prev, contactPhone: e.target.value }))}
                      placeholder="+351 912 345 678"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary me-auto"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : editing ? (
                    'Save Changes'
                  ) : (
                    'Add Supplier'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
